import {
	ActionBar,
	Input,
	SpinnerWithBackground,
	TextArea,
	UnsavedChangesDialog,
	useUnsavedChangesWarning,
} from '@viapeople/components'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { useToast } from '../../../utils/ToastContext'
import { usePecStore } from '../store'
import type { PecGeneralTabInput } from './types'

/**
 * @fileoverview PEC General Tab
 *
 * Basic information for PEC cycle (name, description)
 */

export const PecGeneralTab = () => {
	const { organizationId: _organizationId } = useParams<{
		organizationId: string
		pecId: string
	}>()
	const navigate = useNavigate()
	const toast = useToast()
	const { pecCycle, useCreatePecCycle, useEditPecCycle } = usePecStore()
	const { createPecCycle, isLoading: isCreating } = useCreatePecCycle()
	const { editPecCycle, isLoading: isEditing } = useEditPecCycle(pecCycle?.id)

	const {
		handleSubmit,
		control,
		formState: { errors, isDirty },
		reset,
	} = useForm<PecGeneralTabInput>({
		defaultValues: {
			name: pecCycle?.name || '',
			description: pecCycle?.description || '',
		},
	})

	const { isBlocked, confirmLeave, cancelLeave } = useUnsavedChangesWarning(isDirty)

	useEffect(() => {
		if (pecCycle) {
			reset({
				name: pecCycle.name || '',
				description: pecCycle.description || '',
			})
		}
	}, [pecCycle, reset])

	const handleSubmitInternal = async (data: PecGeneralTabInput) => {
		if (!pecCycle) return

		try {
			if (pecCycle.id) {
				await editPecCycle({
					name: data.name,
					description: data.description || null,
				})
			} else {
				await createPecCycle({
					name: data.name,
					description: data.description || null,
					populationId: null,
					promptText: 'On a scale of 1-5, how well does this person demonstrate the following:',
					ratingScale: 5,
					ratingConfig: null,
					allowNa: false,
					textFeedbackEnabled: true,
					emailTemplateId: null,
					reminderTemplateId: null,
					reminderDays: 0,
					reminderLogic: null,
					reminderMaxEmails: 0,
				})
			}
			reset(data)
		} catch (err) {
			// Mutation errors are handled by the mutation's onError callback.
			// Surface non-mutation errors (e.g., missing store data) to the user.
			if (err instanceof Error && !(err as any).data?.code) {
				toast.showToast(err.message, 'error')
			}
		}
	}

	const isPending = isCreating || isEditing
	const isPecCycleReleased =
		pecCycle?.status === 'active' || pecCycle?.status === 'closed' || pecCycle?.status === 'archived'

	return (
		<form onSubmit={handleSubmit(handleSubmitInternal)} className='relative pb-12 mt-6 w-full'>
			{isPending && <SpinnerWithBackground />}
			<div className='grid grid-cols-1 md:grid-cols-2 gap-x-6 xl:gap-x-12 gap-6 px-6 items-start'>
				<div>
					<Controller
						name='name'
						control={control}
						rules={{
							required: 'Name is required',
							minLength: {
								value: 3,
								message: 'Name must be at least 3 characters',
							},
						}}
						render={({ field }) => (
							<Input
								label='Cycle Name'
								placeholder='e.g., Q1 2024 PEC Cycle'
								variant={errors.name ? 'error' : 'default'}
								hint={
									isPecCycleReleased
										? 'Cannot update cycle name after cycle is launched'
										: (errors.name?.message as string)
								}
								value={field.value}
								onChange={field.onChange}
								onBlur={field.onBlur}
								disabled={isPecCycleReleased}
								required
							/>
						)}
					/>
				</div>
				<div>
					<Controller
						name='description'
						control={control}
						render={({ field }) => (
							<TextArea
								label='Description (Optional)'
								placeholder='Describe the purpose of this PEC cycle'
								value={field.value || ''}
								onChange={field.onChange}
								onBlur={field.onBlur}
								rows={4}
							/>
						)}
					/>
				</div>
			</div>
			<ActionBar
				rightActions={[
					{
						variant: 'secondary' as const,
						label: 'Cancel',
						onClick: () => navigate('/pec'),
					},
					{
						variant: 'primary' as const,
						label: pecCycle?.id ? 'Save' : 'Create Cycle',
						onClick: handleSubmit(handleSubmitInternal),
					},
				]}
			/>
			<UnsavedChangesDialog isBlocked={isBlocked} confirmLeave={confirmLeave} cancelLeave={cancelLeave} />
		</form>
	)
}
